// ResetPassword.jsx — zenithpost.ai
// Route: /reset-password?token=xxx
// Mirrors Login.jsx exactly — same D tokens, same CSS classes, same card pattern

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const D = {
  bg:"#0B0C10", surface:"#0F1117", card:"#13151E",
  border:"rgba(255,255,255,0.07)", accent:"#6C7EF8",
  accentDim:"rgba(108,126,248,0.10)", accentBorder:"rgba(108,126,248,0.22)",
  accentGlow:"rgba(108,126,248,0.28)", success:"#34D399",
  danger:"#F87171", dangerDim:"rgba(248,113,113,0.09)", purple:"#A378F8",
  textPrimary:"#EDEEF5", textSec:"#C4C9DC", textMuted:"#7A82A0",
  fontDisplay:"'Bricolage Grotesque','Plus Jakarta Sans',sans-serif",
  fontBody:"'DM Sans',sans-serif", fontMono:"'JetBrains Mono',monospace",
};

// Same password rules as Signup.jsx
const RULES = [
  { id:"len",     label:"At least 8 characters",  test: p => p.length >= 8 },
  { id:"upper",   label:"One uppercase letter",    test: p => /[A-Z]/.test(p) },
  { id:"lower",   label:"One lowercase letter",    test: p => /[a-z]/.test(p) },
  { id:"number",  label:"One number",              test: p => /\d/.test(p) },
  { id:"special", label:"One special character",   test: p => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const strengthOf = pw => {
  if (!pw) return { score:0, label:"", color:D.border };
  const n = RULES.filter(r => r.test(pw)).length;
  if (n <= 1) return { score:20,  label:"Very weak",   color:"#F87171" };
  if (n === 2) return { score:40,  label:"Weak",        color:"#FB923C" };
  if (n === 3) return { score:60,  label:"Fair",        color:"#F59E0B" };
  if (n === 4) return { score:80,  label:"Strong",      color:"#84CC16" };
  return             { score:100, label:"Very strong", color:"#34D399" };
};

// CSS copied verbatim from Login.jsx — added only new classes, nothing removed
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { background:${D.bg}; color:${D.textPrimary}; font-family:${D.fontBody}; -webkit-font-smoothing:antialiased; }
  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.07); border-radius:99px; }

  @keyframes fadeUp  { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @keyframes pop     { 0%{transform:scale(0.7);opacity:0} 60%{transform:scale(1.12)} 100%{transform:scale(1);opacity:1} }
  @keyframes bar     { from{width:0} to{width:100%} }

  .fu  { animation:fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) both; }
  .d1  { animation-delay:0.07s; }
  .d2  { animation-delay:0.14s; }

  /* Identical to Login.jsx */
  .pg {
    position:fixed; inset:0; z-index:0; pointer-events:none;
    background:
      radial-gradient(ellipse 55% 45% at 20% 10%,rgba(108,126,248,0.07) 0%,transparent 60%),
      radial-gradient(ellipse 40% 35% at 80% 90%,rgba(163,120,248,0.05) 0%,transparent 55%);
  }

  /* ── Form classes — identical to Login.jsx ── */
  .f-group     { margin-bottom:20px; }
  .f-label     { font-family:${D.fontBody}; font-weight:600; font-size:14px; color:${D.textSec}; display:block; margin-bottom:7px; }
  .f-input     { width:100%; padding:13px 16px; border-radius:10px; background:rgba(255,255,255,0.04); border:1px solid ${D.border}; color:${D.textPrimary}; font-family:${D.fontBody}; font-size:15px; outline:none; transition:border-color 0.18s,box-shadow 0.18s; }
  .f-input::placeholder { color:${D.textMuted}; }
  .f-input:focus { border-color:${D.accent}; box-shadow:0 0 0 3px ${D.accentDim}; }
  .f-input.err { border-color:${D.danger}; }
  .f-wrap      { position:relative; }
  .f-eye       { position:absolute; right:13px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:${D.textMuted}; font-size:16px; padding:3px; display:flex; align-items:center; transition:color 0.15s; }
  .f-eye:hover { color:${D.textSec}; }
  .f-err       { margin-top:5px; font-size:12px; color:${D.danger}; font-family:${D.fontBody}; display:flex; align-items:center; gap:4px; }

  /* ── Buttons — identical to Login.jsx ── */
  .btn-p { width:100%; padding:14px; border-radius:11px; border:none; background:linear-gradient(135deg,${D.accent} 0%,#8B72F8 100%); color:white; font-family:${D.fontBody}; font-size:15px; font-weight:700; cursor:pointer; box-shadow:0 6px 24px ${D.accentGlow}; transition:transform 0.15s,box-shadow 0.15s,opacity 0.15s; display:flex; align-items:center; justify-content:center; gap:8px; }
  .btn-p:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 10px 32px rgba(108,126,248,0.42); }
  .btn-p:disabled { opacity:0.55; cursor:not-allowed; }
  .btn-ghost { width:100%; padding:13px; border-radius:11px; border:1px solid ${D.border}; background:rgba(255,255,255,0.04); color:${D.textSec}; font-family:${D.fontBody}; font-size:14px; font-weight:600; cursor:pointer; transition:all 0.18s; margin-top:10px; display:flex; align-items:center; justify-content:center; }
  .btn-ghost:hover { background:rgba(255,255,255,0.07); border-color:rgba(255,255,255,0.15); color:${D.textPrimary}; }

  /* ── Spinner — identical to Login.jsx ── */
  .spinner { width:15px; height:15px; border-radius:50%; border:2px solid rgba(255,255,255,0.25); border-top-color:white; animation:spin 0.7s linear infinite; flex-shrink:0; }

  /* ── Password strength — same as Signup.jsx ── */
  .str-bg   { height:3px; border-radius:99px; background:rgba(255,255,255,0.07); overflow:hidden; margin-top:8px; }
  .str-fill { height:100%; border-radius:99px; transition:width 0.35s,background 0.35s; }
  .rules    { margin-top:10px; padding:12px 14px; border-radius:10px; background:rgba(255,255,255,0.025); border:1px solid ${D.border}; display:flex; flex-direction:column; gap:5px; }
  .rule     { display:flex; align-items:center; gap:7px; font-family:${D.fontMono}; font-size:12px; }
  .r-ok     { color:${D.success}; }
  .r-no     { color:${D.textMuted}; }

  /* ── Success state (new) ── */
  .check-circle { width:72px; height:72px; border-radius:50%; background:rgba(52,211,153,0.10); border:1px solid rgba(52,211,153,0.30); display:flex; align-items:center; justify-content:center; margin:0 auto 22px; animation:pop 0.5s cubic-bezier(0.22,1,0.36,1) both; font-size:34px; color:${D.success}; }
  .bar-track { height:2px; border-radius:99px; background:rgba(255,255,255,0.06); overflow:hidden; margin:22px 0 26px; }
  .bar-fill  { height:100%; border-radius:99px; background:linear-gradient(90deg,${D.success},${D.accent}); animation:bar 3s linear forwards; }

  /* ── Warn state (new) ── */
  .warn-circle { width:64px; height:64px; border-radius:50%; background:rgba(248,113,113,0.09); border:1px solid rgba(248,113,113,0.28); display:flex; align-items:center; justify-content:center; margin:0 auto 20px; font-size:28px; }

  /* ── pulse dot — identical to Login.jsx ── */
  .pulse-dot { width:6px; height:6px; border-radius:50%; background:${D.success}; animation:pulse 2s ease infinite; display:inline-block; }
`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [showPw,    setShowPw]    = useState(false);
  const [showCp,    setShowCp]    = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [errors,    setErrors]    = useState({});
  const [status,    setStatus]    = useState("idle"); // idle | loading | success

  const strength = strengthOf(password);
  const clr = f => setErrors(v => ({ ...v, [f]: undefined }));

  const handleReset = async () => {
    const e = {};
    if (!password)                e.password = "Please enter a new password";
    else if (password.length < 8) e.password = "Password must be at least 8 characters";
    else {
      const fail = RULES.find(r => !r.test(password));
      if (fail) e.password = fail.label + " is required";
    }
    if (!confirm)                 e.confirm = "Please confirm your new password";
    else if (password !== confirm) e.confirm = "Passwords don't match";
    setErrors(e);
    if (Object.keys(e).length) return;

    setStatus("loading");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/api/auth/reset-password`,
        { token, new_password: password }
      );
      setStatus("success");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setStatus("idle");
      setErrors({ general: err.response?.data?.detail || "Reset failed. Your link may have expired — request a new one." });
    }
  };

  const onKey = e => { if (e.key === "Enter") handleReset(); };

  // ─── Logo block — identical to Login.jsx ──────────────────────────────────
  const Logo = () => (
    <div onClick={() => navigate("/")} style={{ cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:12, marginBottom:20 }}>
      <div style={{ width:54, height:54, borderRadius:15, background:`linear-gradient(135deg,${D.accent} 0%,${D.purple} 100%)`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:D.fontDisplay, fontWeight:800, fontSize:26, color:"white", boxShadow:`0 8px 28px ${D.accentGlow}` }}>Z</div>
      <div style={{ fontFamily:D.fontDisplay, fontWeight:700, fontSize:16, color:D.textPrimary }}>zenithpost<span style={{ color:D.accent }}>.ai</span></div>
    </div>
  );

  // ─── No token in URL ───────────────────────────────────────────────────────
  if (!token) return (
    <>
      <style>{CSS}</style>
      <div className="pg" />
      <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 20px", position:"relative", zIndex:1 }}>
        <Logo />
        <div style={{ width:"100%", maxWidth:480, background:D.card, borderRadius:22, border:`1px solid ${D.border}`, boxShadow:"0 32px 80px rgba(0,0,0,0.45)", overflow:"hidden" }}>
          {/* Header — same structure as Login.jsx but danger tint */}
          <div style={{ background:"linear-gradient(135deg,#0D0D12 0%,#1A1A2E 100%)", padding:"34px 36px 30px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 80% 50%,rgba(248,113,113,0.18) 0%,transparent 60%)", pointerEvents:"none" }} />
            <div style={{ fontFamily:D.fontMono, fontSize:10.5, color:"rgba(255,255,255,0.35)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12 }}>Error</div>
            <div style={{ fontFamily:D.fontDisplay, fontWeight:800, fontSize:28, color:"white", lineHeight:1.15, marginBottom:8 }}>Invalid reset link</div>
            <div style={{ fontSize:14.5, color:"rgba(255,255,255,0.48)", lineHeight:1.65 }}>This link is missing or has been corrupted.</div>
          </div>
          <div style={{ padding:"36px 36px 40px", textAlign:"center" }}>
            <div className="warn-circle">⚠</div>
            <p style={{ fontSize:14.5, color:D.textSec, lineHeight:1.75, marginBottom:28 }}>
              This password reset link is invalid. It may have been copied incorrectly or the token is missing.
              Go back to the login page to request a new one.
            </p>
            <button className="btn-p" onClick={() => navigate("/login")}>← Back to login</button>
          </div>
        </div>
        <div style={{ marginTop:24, textAlign:"center" }}>
          <p style={{ fontFamily:D.fontMono, fontSize:10.5, color:D.textMuted }}>© {new Date().getFullYear()} zenithpost.ai · Secure password reset</p>
        </div>
      </div>
    </>
  );

  // ─── Main page ─────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <div className="pg" />
      <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 20px", position:"relative", zIndex:1 }}>

        {/* Logo */}
        <div className="fu">
          <Logo />
        </div>

        {/* Card — same exact structure as Login.jsx */}
        <div className="fu d1" style={{ width:"100%", maxWidth:480, background:D.card, borderRadius:22, border:`1px solid ${D.border}`, boxShadow:"0 32px 80px rgba(0,0,0,0.45)", overflow:"hidden" }}>

          {/* Card header */}
          <div style={{ background:"linear-gradient(135deg,#0D0D12 0%,#1A1A2E 100%)", padding:"34px 36px 30px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 80% 50%,rgba(108,126,248,0.28) 0%,transparent 60%)", pointerEvents:"none" }} />
            <div style={{ fontFamily:D.fontMono, fontSize:10.5, color:"rgba(255,255,255,0.35)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12 }}>
              {status === "success" ? "All done" : "Security"}
            </div>
            <div style={{ fontFamily:D.fontDisplay, fontWeight:800, fontSize:28, color:"white", lineHeight:1.15, marginBottom:8 }}>
              {status === "success" ? "Password updated" : "Set a new password"}
            </div>
            <div style={{ fontSize:14.5, color:"rgba(255,255,255,0.48)", lineHeight:1.65 }}>
              {status === "success"
                ? "You're all set. Redirecting to login in a moment…"
                : "Choose a strong password for your zenithpost.ai account."}
            </div>
          </div>

          {/* Card body — same padding as Login.jsx */}
          <div style={{ padding:"32px 36px 36px" }}>

            {/* ── SUCCESS ─────────────────────────────────────── */}
            {status === "success" && (
              <div style={{ textAlign:"center" }}>
                <div className="check-circle">✓</div>
                <div style={{ fontFamily:D.fontMono, fontSize:10.5, color:D.success, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>
                  Password changed
                </div>
                <p style={{ fontSize:15, color:D.textSec, lineHeight:1.72 }}>
                  Your password has been updated successfully. You'll be taken to the login page automatically.
                </p>
                <div className="bar-track"><div className="bar-fill" /></div>
                <button className="btn-p" onClick={() => navigate("/login")}>Go to login →</button>
              </div>
            )}

            {/* ── FORM ────────────────────────────────────────── */}
            {status !== "success" && (
              <>
                {/* Error banner — same style as Login.jsx */}
                {errors.general && (
                  <div style={{ display:"flex", gap:10, padding:"12px 16px", marginBottom:20, borderRadius:10, background:"rgba(248,113,113,0.09)", border:"1px solid rgba(248,113,113,0.22)" }}>
                    <span style={{ fontSize:15, flexShrink:0 }}>⚠</span>
                    <span style={{ fontSize:13, color:D.danger, lineHeight:1.55 }}>{errors.general}</span>
                  </div>
                )}

                {/* New password field */}
                <div className="f-group">
                  <label className="f-label">New password</label>
                  <div className="f-wrap">
                    <input
                      className={`f-input${errors.password ? " err" : ""}`}
                      type={showPw ? "text" : "password"}
                      placeholder="Min. 8 characters"
                      value={password}
                      onChange={e => { setPassword(e.target.value); clr("password"); }}
                      onFocus={() => setPwFocused(true)}
                      onBlur={() => setPwFocused(false)}
                      onKeyDown={onKey}
                      style={{ paddingRight:44 }}
                    />
                    <button className="f-eye" type="button" onClick={() => setShowPw(s => !s)} tabIndex={-1}>
                      {showPw ? "🙈" : "👁"}
                    </button>
                  </div>
                  {errors.password && <div className="f-err">⚠ {errors.password}</div>}

                  {/* Strength bar */}
                  {password && (
                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginTop:7, marginBottom:4 }}>
                        <span style={{ fontFamily:D.fontMono, fontSize:10, color:D.textMuted }}>Strength</span>
                        <span style={{ fontFamily:D.fontMono, fontSize:10, fontWeight:700, color:strength.color }}>{strength.label}</span>
                      </div>
                      <div className="str-bg">
                        <div className="str-fill" style={{ width:`${strength.score}%`, background:strength.color }} />
                      </div>
                    </div>
                  )}

                  {/* Rules checklist */}
                  {(pwFocused || password) && (
                    <div className="rules">
                      {RULES.map(r => {
                        const ok = r.test(password);
                        return (
                          <div key={r.id} className={`rule ${ok ? "r-ok" : "r-no"}`}>
                            <span>{ok ? "✓" : "○"}</span>
                            <span>{r.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Confirm password field */}
                <div className="f-group">
                  <label className="f-label">Confirm new password</label>
                  <div className="f-wrap">
                    <input
                      className={`f-input${errors.confirm ? " err" : ""}`}
                      type={showCp ? "text" : "password"}
                      placeholder="Re-enter your password"
                      value={confirm}
                      onChange={e => { setConfirm(e.target.value); clr("confirm"); }}
                      onKeyDown={onKey}
                      style={{ paddingRight:44 }}
                    />
                    <button className="f-eye" type="button" onClick={() => setShowCp(s => !s)} tabIndex={-1}>
                      {showCp ? "🙈" : "👁"}
                    </button>
                  </div>
                  {errors.confirm && <div className="f-err">⚠ {errors.confirm}</div>}

                  {/* Live match pill */}
                  {confirm && password && !errors.confirm && (
                    <div className={`rule ${password === confirm ? "r-ok" : "r-no"}`} style={{ marginTop:6 }}>
                      <span>{password === confirm ? "✓" : "○"}</span>
                      <span>{password === confirm ? "Passwords match" : "Passwords don't match yet"}</span>
                    </div>
                  )}
                </div>

                {/* Buttons */}
                <div style={{ marginTop:28 }}>
                  <button className="btn-p" onClick={handleReset} disabled={status === "loading"}>
                    {status === "loading"
                      ? <><div className="spinner" />Updating password…</>
                      : "Update password →"}
                  </button>
                  <button className="btn-ghost" onClick={() => navigate("/login")}>
                    ← Back to login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer — identical to Login.jsx */}
        <div className="fu d2" style={{ marginTop:24, textAlign:"center" }}>
          <p style={{ fontFamily:D.fontMono, fontSize:10.5, color:D.textMuted }}>
            © {new Date().getFullYear()} zenithpost.ai · Secure password reset
          </p>
        </div>

      </div>
    </>
  );
}